import React from 'react';
import { FallbackProps } from 'react-error-boundary';
import { ErrorBoundaryStyle } from './ErrorBoundary.style';
import { inferClientDeploymentTier } from '../../config/platformUrls';

const ErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const errorMessage = error instanceof Error ? error.message : String(error ?? 'Unknown error');
  const errorStack = error instanceof Error ? error.stack : undefined;
  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/home';
  };

  return (
    <ErrorBoundaryStyle>
      <div className="error-container">
        <div className="error-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>

        <h1 className="error-title">Something went wrong</h1>

        <p className="error-message">
          We're sorry, but an unexpected error occurred while loading this page.
          Our team has been notified and is working to fix the issue.
        </p>

        {(process.env.NODE_ENV === 'development' || inferClientDeploymentTier() === 'dev') && (
          <details className="error-details">
            <summary style={{ cursor: 'pointer', marginBottom: '8px', color: '#ffffff' }}>
              Error Details (Development Only)
            </summary>
            {errorMessage}
            {errorStack && (
              <>
                {'\n\n'}
                {errorStack}
              </>
            )}
          </details>
        )}

        <div className="error-actions">
          <button className="error-button" onClick={handleReload}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
            </svg>
            Try Again
          </button>

          <button className="error-button secondary" onClick={handleGoHome}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9,22 9,12 15,12 15,22" />
            </svg>
            Go Home
          </button>

          {process.env.NODE_ENV === 'development' && (
            <button
              className="error-button secondary"
              onClick={() => {
                // Test error for development
                throw new Error('Test error from ErrorBoundary fallback');
              }}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                marginTop: '8px'
              }}
            >
              🧪 Test Error (Dev Only)
            </button>
          )}
        </div>
      </div>
    </ErrorBoundaryStyle>
  );
};

export default ErrorFallback;
